import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { academicService, adminService, enrollmentService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Card, Badge, Button, Modal, Input, EmptyState, Spinner, Pagination } from '../../components/ui';
import { getApiErrorMessage } from '../../utils/apiErrors';

const PAGE_SIZE = 20;

function AdminTeachers() {
  const { addNotification } = useNotification();
  const { t } = useTranslation();

  const [teachers, setTeachers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [featuringId, setFeaturingId] = useState(null);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  // Courses modal — the teacher's own course list, each expandable in place
  // to show its active roster (fetched lazily per course, not all at once,
  // since a teacher can have many courses and each roster call isn't free).
  const [coursesTeacher, setCoursesTeacher] = useState(null);
  const [expandedModuleId, setExpandedModuleId] = useState(null);
  const [rosterCache, setRosterCache] = useState({});
  const [rosterLoadingId, setRosterLoadingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      adminService.getUsers({ role: 'teacher', search: search || undefined, page, page_size: PAGE_SIZE }),
      academicService.getModules({ page_size: 100 }),
    ])
      .then(([teachersRes, modulesRes]) => {
        const teachersData = teachersRes.data;
        setTeachers(Array.isArray(teachersData) ? teachersData : teachersData.results || []);
        setCount(Array.isArray(teachersData) ? teachersData.length : teachersData.count || 0);
        const modulesData = modulesRes.data;
        setModules(Array.isArray(modulesData) ? modulesData : modulesData.results || []);
      })
      .catch(() => addNotification(t('Failed to load teachers'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const coursesOf = (teacherId) => modules.filter((m) => (m.teachers || []).some((tch) => tch.id === teacherId));

  const filteredTeachers = teachers.filter((tch) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${tch.first_name} ${tch.last_name}`.toLowerCase().includes(q) || tch.email.toLowerCase().includes(q);
  });

  const openCourses = (teacher) => {
    setCoursesTeacher(teacher);
    setExpandedModuleId(null);
  };

  const toggleFeatured = async (teacher) => {
    const profile = teacher.teacher_profile;
    if (!profile) return;
    setFeaturingId(profile.id);
    try {
      const res = await adminService.setTeacherFeatured(profile.id, !profile.is_featured, profile.featured_order || 0);
      setTeachers((current) => current.map((item) => (
        item.id === teacher.id ? { ...item, teacher_profile: res.data } : item
      )));
      addNotification(profile.is_featured ? t('Teacher removed from featured list') : t('Teacher added to featured list'), 'success');
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to update featured teacher'), 'error');
    } finally {
      setFeaturingId(null);
    }
  };

  const toggleModule = async (moduleId) => {
    if (expandedModuleId === moduleId) {
      setExpandedModuleId(null);
      return;
    }
    setExpandedModuleId(moduleId);
    if (rosterCache[moduleId]) return;
    setRosterLoadingId(moduleId);
    try {
      const res = await enrollmentService.getModuleEnrollments(moduleId, 'active');
      const data = res.data;
      setRosterCache((prev) => ({ ...prev, [moduleId]: Array.isArray(data) ? data : data.results || [] }));
    } catch (e) {
      addNotification(t('Failed to load roster'), 'error');
    } finally {
      setRosterLoadingId(null);
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Teachers')} subtitle={t('See each teacher\'s courses and enrolled students')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <Input
              placeholder={t('Search by name or email...')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="sm:max-w-xs"
            />
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : filteredTeachers.length === 0 ? (
            <Card><EmptyState title={t('No teachers yet')} /></Card>
          ) : (
            <Card padded={false} className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">{t('Teacher')}</th>
                      <th className="text-left px-4 py-3">{t('Status')}</th>
                      <th className="text-left px-4 py-3">{t('Featured')}</th>
                      <th className="text-left px-4 py-3">{t('Courses')}</th>
                      <th className="text-left px-4 py-3">{t('Students')}</th>
                      <th className="text-right px-4 py-3">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredTeachers.map((tch) => {
                      const teacherCourses = coursesOf(tch.id);
                      const totalStudents = teacherCourses.reduce((sum, m) => sum + (m.students_count || 0), 0);
                      return (
                        <tr key={tch.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {tch.profile_picture ? (
                                <img src={tch.profile_picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-semibold shrink-0">
                                  {tch.first_name?.[0]}{tch.last_name?.[0]}
                                </div>
                              )}
                              <div>
                                <div className="font-medium text-gray-900">{tch.first_name} {tch.last_name}</div>
                                <div className="text-xs text-gray-400">{tch.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {tch.teacher_profile?.is_featured ? <Badge tone="yellow">{t('Featured')}</Badge> : <span className="text-xs text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {tch.teacher_profile?.status ? (
                              <Badge status={tch.teacher_profile.status}>{t(tch.teacher_profile.status)}</Badge>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{teacherCourses.length}</td>
                          <td className="px-4 py-3 text-gray-600">{totalStudents}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant={tch.teacher_profile?.is_featured ? 'secondary' : 'primary'}
                                onClick={() => toggleFeatured(tch)}
                                disabled={!tch.teacher_profile || tch.teacher_profile.status !== 'approved' || featuringId === tch.teacher_profile.id}
                              >
                                {tch.teacher_profile?.is_featured ? t('Remove feature') : t('Feature teacher')}
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => openCourses(tch)} disabled={teacherCourses.length === 0}>
                                {t('View Courses')}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </Card>
          )}
        </main>
      </div>

      {/* Courses + rosters modal — click a course to expand its active
          student list in place, fetched on demand and cached per course. */}
      <Modal
        open={!!coursesTeacher}
        onClose={() => setCoursesTeacher(null)}
        title={coursesTeacher ? t("{{name}}'s Courses", { name: `${coursesTeacher.first_name} ${coursesTeacher.last_name}` }) : ''}
        size="lg"
      >
        {coursesTeacher && (
          <div className="space-y-2 max-h-[28rem] overflow-y-auto">
            {coursesOf(coursesTeacher.id).map((m) => (
              <div key={m.id} className="border border-gray-100 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleModule(m.id)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 text-left"
                >
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{m.name}</div>
                    <div className="text-xs text-gray-400">{m.code}</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="flex flex-wrap gap-1 justify-end">
                      {(m.grades || []).map((g) => <Badge key={g.id} tone="blue">{g.name}</Badge>)}
                    </div>
                    <span>{t('{{count}} students', { count: m.students_count ?? 0 })}</span>
                    <svg
                      className={`w-4 h-4 shrink-0 transition-transform ${expandedModuleId === m.id ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {expandedModuleId === m.id && (
                  <div className="border-t border-gray-100 bg-gray-50 px-3 py-2">
                    {rosterLoadingId === m.id ? (
                      <div className="py-4 flex justify-center"><Spinner size={5} /></div>
                    ) : (rosterCache[m.id] || []).length === 0 ? (
                      <p className="text-xs text-gray-500 py-2">{t('No students enrolled yet')}</p>
                    ) : (
                      <div className="space-y-1.5 py-1">
                        {(rosterCache[m.id] || []).map((enr) => (
                          <div key={enr.id} className="flex items-center justify-between text-sm">
                            <span className="text-gray-800">{enr.student?.first_name} {enr.student?.last_name}</span>
                            <span className="text-xs text-gray-400">{enr.student?.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AdminTeachers;
